import type { ChapterBlock } from "../../types/VideoTypes";

export interface NormalizedVideoChapter extends ChapterBlock {
  startTime: number;
}

/**
 * Normalizes a list of video chapters by:
 * - Converting the `seconds` property to a number and storing it in `startTime`.
 * - Filtering out chapters with invalid or out-of-bounds start times.
 * - Sorting chapters by their start time.
 * - Removing duplicate chapters with the same start time.
 *
 * @param chapters The list of video chapters to normalize.
 * @param duration The total duration of the video.
 * @param introductionTitle The title for the default introduction chapter.
 *
 * @returns The list of normalized video chapters.
 *
 * @example
 * ```ts
 * const chapters: ChapterBlock[] = [
 *   {
 *     _uid: "chapter-1",
 *     component: "chapter",
 *     title: "Main topic",
 *     seconds: "30",
 *   },
 * ];
 *
 * normalizeVideoChapters(chapters, 120, "Introduction");
 * Returns:
 * [
 *   {
 *     _uid: "default-introduction",
 *     component: "chapter",
 *     title: "Introduction",
 *     seconds: "0",
 *     startTime: 0,
 *   },
 *   {
 *     _uid: "chapter-1",
 *     component: "chapter",
 *     title: "Main topic",
 *     seconds: "30",
 *     startTime: 30,
 *   },
 * ];
 * ```
 */
export function normalizeVideoChapters(
  chapters: ChapterBlock[],
  duration: number,
  introductionTitle: string
): NormalizedVideoChapter[] {
  const normalizedChapters = chapters
    .map((chapter) => ({
      ...chapter,
      startTime: Number(chapter.seconds),
    }))
    .filter(
      (chapter) =>
        Number.isFinite(chapter.startTime) &&
        chapter.startTime >= 0 &&
        chapter.startTime < duration
    )
    .sort((first, second) => first.startTime - second.startTime)
    .filter(
      (chapter, index, allChapters) =>
        index === 0 || chapter.startTime !== allChapters[index - 1].startTime
    );

  if (
    normalizedChapters.length === 0 ||
    normalizedChapters[0].startTime === 0
  ) {
    return normalizedChapters;
  }

  return [
    {
      _uid: "default-introduction",
      component: "chapter",
      title: introductionTitle,
      seconds: "0",
      startTime: 0,
    },
    ...normalizedChapters,
  ];
}
